/**
 * useEmailSendMode - read the live outbound email mode for the
 * AppShell banner.
 *
 * Open to every authenticated user (the banner must render on
 * customer-portal sessions too if Greg routes one through during
 * testing). The query refetches every 60 seconds so an admin flip
 * in another tab propagates within a minute without a hard reload.
 *
 * The admin-side card (``EmailSendRulesCard``) calls
 * ``queryClient.invalidateQueries(EMAIL_SEND_MODE_QUERY_KEY)`` on
 * save so its own tab sees the change immediately.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { EmailSendRulesPayload } from "@/types/api";

export const EMAIL_SEND_MODE_QUERY_KEY = ["me", "email-send-mode"] as const;

export function useEmailSendMode(): UseQueryResult<EmailSendRulesPayload> {
  return useQuery<EmailSendRulesPayload>({
    queryKey: EMAIL_SEND_MODE_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<EmailSendRulesPayload>(
        "/me/email-send-mode",
      );
      return response.data;
    },
    // 60s refetch matches D10 in the plan: an admin toggle propagates
    // to every tab within a minute. Stale window is shorter so a
    // tab that comes back into focus refetches quickly.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
